import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import api from '../services/api.js';
import { useAuth } from '../utils/AuthContext.jsx';
import { resolvePostLoginRoute } from '../utils/menuConfig.js';
import './Login.css';

const FEATURES = [
  {
    icon: 'fa-solid fa-store',
    title: 'Multi-Brand, Multi-Outlet Control',
    desc: 'Manage every brand, outlet, and commissary station from a single workspace. Define cut-off rules per brand, assign roles scoped to specific outlets, and keep master data consistent across your entire operation — no spreadsheets, no silos.',
    accent: '#2dd4bf',
  },
  {
    icon: 'fa-solid fa-boxes-stacking',
    title: 'Pull Order Lifecycle Management',
    desc: 'Outlet managers submit pull orders before cut-off. Commissary supervisors acknowledge, reject with reason, or edit quantities. Orders track through Submitted → Acknowledged → In Production → Dispatched → Received with a full audit trail at every step.',
    accent: '#60a5fa',
  },
  {
    icon: 'fa-solid fa-calendar-check',
    title: 'Production Scheduling & Kitchen Slots',
    desc: 'Book kitchen slots and schedule production runs against your real capacity. Conflict detection prevents double-booking. Record batch yields and production completion to close the loop between demand and supply — automatically.',
    accent: '#a78bfa',
  },
  {
    icon: 'fa-solid fa-flask',
    title: 'Recipe & Bill of Materials Costing',
    desc: 'Build versioned recipes with ingredient quantities, units, and standard costs. KitchenOS calculates batch cost automatically and tracks variance against actual spend — so you always know your real cost per SKU before you commit to production.',
    accent: '#f59e0b',
  },
  {
    icon: 'fa-solid fa-layer-group',
    title: 'Live Stock Ledger & Smart Alerts',
    desc: 'Monitor raw material inventory across all locations in real time. Set safety-stock thresholds and KitchenOS raises low-stock alerts before you run out. Track expiring batches, log receipts, and trigger reorder requests — all from one screen.',
    accent: '#34d399',
  },
  {
    icon: 'fa-solid fa-chart-bar',
    title: 'Brand Cost Allocation & Audit Log',
    desc: 'See standard cost vs actual cost per brand with variance reporting. Every action — from master data changes to order status transitions — is captured in an immutable audit log filterable by module, actor, and date range.',
    accent: '#fb7185',
  },
];

const HELP_SECTIONS = [
  {
    heading: 'Getting Started',
    icon: 'fa-solid fa-rocket',
    items: [
      { q: 'How do I log in?', a: 'Enter your work email and password, then click Sign in. If you\'ve forgotten your password, contact your system administrator to reset it.' },
      { q: 'Who can access KitchenOS?', a: 'Access is role-based. Roles include Super Admin, Finance, Brand Manager, Commissary Supervisor, Outlet Manager, and Procurement Staff. Your admin assigns your role.' },
      { q: 'What if I see "Forbidden"?', a: 'Your role may not have access to that page. Contact your Super Admin to review your role or outlet assignment.' },
    ],
  },
  {
    heading: 'Pull Orders',
    icon: 'fa-solid fa-boxes-stacking',
    items: [
      { q: 'How do I submit a pull order?', a: 'Go to Pull Order Entry, select your outlet and delivery date, add items with quantities, then click Submit. Orders must be submitted before the brand\'s cut-off time.' },
      { q: 'Can I edit a submitted order?', a: 'Yes — Outlet Managers can edit submitted orders before the cut-off time. After cut-off, only a Commissary Supervisor can make changes.' },
      { q: 'What happens after I submit?', a: 'The order moves to Submitted status. A Commissary Supervisor will Acknowledge or Reject it. You\'ll see the updated status on your dashboard.' },
    ],
  },
  {
    heading: 'Production & Scheduling',
    icon: 'fa-solid fa-calendar-check',
    items: [
      { q: 'How do I book a kitchen slot?', a: 'Open Production Schedule, select a date and kitchen station, choose the items to produce, set quantities, and click Book Slot. Conflicts are highlighted automatically.' },
      { q: 'How do I record production completion?', a: 'Open the scheduled run, enter the actual batch yield per item, and click Complete. Stock levels are updated automatically.' },
    ],
  },
  {
    heading: 'Recipes & Costing',
    icon: 'fa-solid fa-flask',
    items: [
      { q: 'How do I create a recipe?', a: 'Go to Master Recipe & BOM, click New Recipe, add ingredients with quantities and units, set the batch size, and save. KitchenOS calculates the total batch cost from ingredient standard prices.' },
      { q: 'What is a recipe version?', a: 'Each time you edit and save a recipe, a new version is created (v1.0, v1.1, …). Previous versions are preserved for cost history and audit purposes.' },
    ],
  },
  {
    heading: 'Inventory & Alerts',
    icon: 'fa-solid fa-layer-group',
    items: [
      { q: 'How do I log a stock receipt?', a: 'Go to Raw Material Receipt & Stock Ledger, click Log Receipt, select the item and supplier, enter quantity and expiry date, and confirm. Stock is updated immediately.' },
      { q: 'How do alerts work?', a: 'KitchenOS compares current stock against your safety-stock threshold. If stock falls below threshold or a batch is expiring soon, an alert appears in your Inventory Alert Digest.' },
      { q: 'How do I raise a reorder request?', a: 'Click the Reorder button on a low-stock alert, confirm the item and suggested quantity, and submit. Procurement Staff will see the request in their queue.' },
    ],
  },
  {
    heading: 'Transfers & Delivery',
    icon: 'fa-solid fa-truck',
    items: [
      { q: 'How do I dispatch a transfer?', a: 'Go to Transfer Dispatch & Receipt, click New Transfer, select the destination outlet, add items and quantities, enter driver details, and click Dispatch.' },
      { q: 'How does the receiving outlet confirm?', a: 'The outlet manager opens the Transfers page, finds the inbound transfer, enters received quantities (noting any discrepancies), and clicks Confirm Receipt.' },
    ],
  },
];

const AUTO_ROTATE_MS = 5000;

// One-click demo logins by role. Lets anyone try the product as a specific
// role without typing credentials. Also reachable via /login?demo=<key> so the
// marketing site can deep-link straight into a role. These are the shared
// demo-environment logins — not customer data.
const DEMO_ACCOUNTS = [
  { key: 'superadmin',  name: 'KitchenOS Admin',       role: 'Super Admin',            email: 'admin@kitchenos.local',         password: 'Admin@12345' },
  { key: 'finance',     name: 'Finance Officer',       role: 'Finance',                email: 'finance@kitchenos.local',       password: 'Demo@12345'  },
  { key: 'brand',       name: 'Brand Manager',         role: 'Brand Manager',          email: 'brand.manager@kitchenos.local', password: 'Demo@12345'  },
  { key: 'commissary',  name: 'Commissary Supervisor', role: 'Commissary Supervisor',  email: 'commissary@kitchenos.local',    password: 'Demo@12345'  },
  { key: 'outlet',      name: 'Outlet Manager — BGC',  role: 'Outlet Manager',         email: 'outlet.bgc@kitchenos.local',    password: 'Demo@12345'  },
  { key: 'procurement', name: 'Procurement Staff',     role: 'Procurement',            email: 'procurement@kitchenos.local',   password: 'Demo@12345'  },
];

export default function Login() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail]          = useState('');
  const [password, setPassword]    = useState('');
  const [error, setError]          = useState('');
  const [busy, setBusy]            = useState(false);
  const [activeFeature, setActive] = useState(0);
  const [helpOpen, setHelpOpen]    = useState(false);
  const [expanded, setExpanded]    = useState({});

  useEffect(() => {
    const t = setInterval(() => setActive((p) => (p + 1) % FEATURES.length), AUTO_ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  // Close help drawer on Escape
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') setHelpOpen(false); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Deep-link: /login?demo=<role> signs in as that demo account in one step.
  useEffect(() => {
    if (isAuthenticated) return;
    const key = new URLSearchParams(window.location.search).get('demo');
    const acct = key && DEMO_ACCOUNTS.find((a) => a.key === key);
    if (acct) {
      setEmail(acct.email);
      setPassword(acct.password);
      doLogin(acct.email, acct.password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticated) return <Navigate to={resolvePostLoginRoute()} replace />;

  async function doLogin(emailArg, passwordArg) {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login', { email: emailArg, password: passwordArg });
      login(data.token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    await doLogin(email, password);
  }

  function quickLogin(acct) {
    setEmail(acct.email);
    setPassword(acct.password);
    doLogin(acct.email, acct.password);
  }

  function toggleItem(key) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const f = FEATURES[activeFeature];

  return (
    <div className="login-split">
      {/* ── Help icon (top-right) ── */}
      <button
        className="help-toggle"
        onClick={() => setHelpOpen(true)}
        aria-label="Open help documentation"
        data-testid="help-toggle"
      >
        <i className="fa-solid fa-circle-question" />
      </button>

      {/* ── Help drawer ── */}
      {helpOpen && (
        <div className="help-backdrop" onClick={() => setHelpOpen(false)} aria-hidden="true" />
      )}
      <aside className={`help-drawer${helpOpen ? ' help-drawer--open' : ''}`} aria-label="Help documentation">
        <div className="help-drawer-header">
          <div>
            <div className="help-drawer-eyebrow">Documentation</div>
            <h2 className="help-drawer-title">How to use KitchenOS</h2>
          </div>
          <button className="help-drawer-close" onClick={() => setHelpOpen(false)} aria-label="Close help">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="help-drawer-body">
          {HELP_SECTIONS.map((section) => (
            <div className="help-section" key={section.heading}>
              <div className="help-section-heading">
                <i className={section.icon} />
                {section.heading}
              </div>
              {section.items.map((item, idx) => {
                const key = `${section.heading}-${idx}`;
                const open = expanded[key];
                return (
                  <div className="help-item" key={key}>
                    <button
                      className={`help-item-q${open ? ' help-item-q--open' : ''}`}
                      onClick={() => toggleItem(key)}
                      aria-expanded={open}
                    >
                      <span>{item.q}</span>
                      <i className={`fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                    </button>
                    {open && <div className="help-item-a">{item.a}</div>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="help-drawer-footer">
          <i className="fa-solid fa-headset" />
          Need more help? Contact your system administrator.
        </div>
      </aside>

      {/* ── Left panel: rotating help documentation ── */}
      <div className="login-left">
        <div className="login-watermark">KitchenOS</div>

        <div className="login-brand">
          <div className="login-brand-icon">
            <i className="fa-solid fa-utensils" />
          </div>
          <span className="login-brand-name">KitchenOS</span>
        </div>

        <p className="login-left-eyebrow">Manage · Schedule · Cost · Track</p>

        <h1 className="login-headline">
          The operating system<br />for <em>modern</em> kitchens.
        </h1>

        <p className="login-sub">
          Centralise your commissary — from pull orders and production
          scheduling to recipe costing and live stock alerts.
        </p>

        {/* Rotating feature card */}
        <div className="login-feature-card" key={activeFeature}>
          <div className="login-feature-icon" style={{ background: `${f.accent}18`, color: f.accent }}>
            <i className={f.icon} />
          </div>
          <div>
            <h3 className="login-feature-title">{f.title}</h3>
            <p className="login-feature-desc">{f.desc}</p>
          </div>
        </div>

        {/* Dot navigation */}
        <div className="login-dots" role="tablist" aria-label="Feature navigation">
          {FEATURES.map((_, i) => (
            <button
              key={i}
              className={`login-dot${i === activeFeature ? ' login-dot--active' : ''}`}
              onClick={() => setActive(i)}
              aria-label={`Show feature ${i + 1}: ${FEATURES[i].title}`}
            />
          ))}
        </div>

        <p className="login-left-footer">Central kitchen · Multi-brand · Real-time</p>
      </div>

      {/* ── Right panel: login form ── */}
      <div className="login-right">
        <div className="login-form-wrap" data-testid="login-card">
          <div className="login-form-header">
            <div className="login-form-eyebrow">Secure access</div>
            <h2 className="login-form-title">Welcome back.</h2>
            <p className="login-form-sub">Sign in to your KitchenOS workspace.</p>
          </div>

          <form onSubmit={onSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="login-email">Email address</label>
              <input
                id="login-email"
                type="email"
                className="login-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="username"
                data-testid="login-email"
              />
            </div>

            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="login-error" data-testid="login-error">
                <i className="fa-solid fa-circle-exclamation" />
                {error}
              </div>
            )}

            <button
              type="submit"
              className="login-submit"
              disabled={busy}
              data-testid="login-submit"
            >
              {busy ? (
                <><i className="fa-solid fa-spinner fa-spin" /> Signing in…</>
              ) : (
                <><i className="fa-solid fa-arrow-right-to-bracket" /> Sign in</>
              )}
            </button>
          </form>

          {/* ── Demo accounts: one-click login by role ── */}
          <div className="login-demo" data-testid="demo-accounts">
            <div className="login-demo-head">
              <i className="fa-solid fa-flask" /> Demo accounts — try a role
            </div>
            <div className="login-demo-list">
              {DEMO_ACCOUNTS.map((acct) => (
                <button
                  key={acct.key}
                  type="button"
                  className="login-demo-btn"
                  onClick={() => quickLogin(acct)}
                  disabled={busy}
                  data-testid={`demo-login-${acct.key}`}
                >
                  <span className="login-demo-name">{acct.name}</span>
                  <span className="login-demo-role">{acct.role}</span>
                  <i className="fa-solid fa-arrow-right-to-bracket login-demo-go" />
                </button>
              ))}
            </div>
          </div>

          <div className="login-form-footer">
            <button className="help-inline-btn" onClick={() => setHelpOpen(true)}>
              <i className="fa-solid fa-circle-question" /> Need help signing in?
            </button>
            <span className="help-inline-sep">·</span>
            © {new Date().getFullYear()} KitchenOS · Blast Asia Inc.
          </div>
        </div>
      </div>
    </div>
  );
}
